import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import moment from 'moment';
import { Moment } from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, of, Subject } from 'rxjs';
import { finalize, first, map } from 'rxjs/operators';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { DictionaryItem } from 'src/app/models/DictionaryItem';
import { LegalizationStatusEnum } from '../../../models/enums/legalization-status-enum';
import { LegalizationService } from '../../../data/legalization.service';
import { WorkerLegalizationActiveFileDto } from '../../../models/dtos/worker-legalization-active-file-dto';
import { LegalizationInformationDto as LegalizationInformationDto } from '../../../models/dtos/LegalizationInformationDto';
import { LegalizationDocumentFileTypeDto } from '../../../models/dtos/legalization-document-files-type-dto';
import { SaveLegalizationInfoBaseComponent } from '../base/save-legalization-info-base.component';
import { AddLegalizationInformationModalData } from '../AddLegalizationInformationModalData';
import { LegalizationDocumentInformationDto } from '../../../models/dtos/legalization-document-information-dto';
import { CorrectLegalizationDocumentBasicDto } from '../../../models/dtos/legalization-document-files-dto';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { LegalizationAlertStatusEnum } from 'src/app/models/enums/legalization-alert-status-enum';

@Component({
  selector: 'app-save-legalization-info-by-legalizer-modal',
  templateUrl: './save-legalization-info-by-legalizer-modal.component.html',
  styleUrl: './save-legalization-info-by-legalizer-modal.scss',
  standalone: false
})
export class SaveLegalizationInfoByLegalizerModalComponent extends SaveLegalizationInfoBaseComponent implements OnInit, OnDestroy {
  dialogRef = inject<MatDialogRef<AddLegalizationInformationModalData>>(MatDialogRef);
  data = inject<AddLegalizationInformationModalData>(MAT_DIALOG_DATA);
  private formBuilder = inject(UntypedFormBuilder);
  private spinner = inject(NgxSpinnerService);
  protected dictionaryService: DictionaryService;
  private legalizationService = inject(LegalizationService);
  private snackbar = inject(SnackBarService);

  public listOfLegalizationDocumentStatuses$: Observable<DictionaryItem[]> = of([]);
  public listOfLegalizationStatuses$: Observable<DictionaryItem[]> = of([]);
  public listOfWorkerLegalizationActiveFiles$: Observable<WorkerLegalizationActiveFileDto[]> = of([]);
  public listOfLegalizationAlertStatusesForForms$: Observable<DictionaryItem[]> = of([]);

  public isDocumentUploaded: boolean = false;
  public formGroup: UntypedFormGroup;
  public hasExpirationStartDate: boolean = false;
  public hasExpirationEndDate: boolean = false;

  private legalizationFileTypeName: string;

  selectedDocument: LegalizationDocumentInformationDto | null;

  private readonly unsubscribe$ = new Subject<void>();

  @ViewChild('autosize') autosize: CdkTextareaAutosize;

  preSelectedArray: number[] = [];


  constructor() {
    super();
  }

  get legalizationDocumentTypeId() {
    return this.formGroup.get('legalizationDocumentTypeId') as UntypedFormControl;
  }
  get startDate() {
    return this.formGroup.get('startDate') as UntypedFormControl;
  }
  get endDate() {
    return this.formGroup.get('endDate') as UntypedFormControl;
  }
  get description() {
    return this.formGroup.get('description') as UntypedFormControl;
  }
  get documentCategory() {
    return this.formGroup.get('documentCategory') as UntypedFormControl;
  }
  get documentStatus() {
    return this.formGroup.get('documentStatus') as UntypedFormControl;
  }
  get comment() {
    return this.formGroup.get('comment') as UntypedFormControl;
  }
  get status() {
    return this.formGroup.get('status') as UntypedFormControl;
  }
  get legalizationsIds() {
    return this.formGroup.get('agreements') as UntypedFormControl;
  }
  get documentNumber() {
    return this.formGroup.get('documentNumber') as UntypedFormControl;
  }
  get submissionDate() {
    return this.formGroup.get('submissionDate') as UntypedFormControl;
  }
  get alertStatusId() {
    return this.formGroup.get('alertStatusId') as UntypedFormControl;
  }

  ngOnInit(): void {
    this.createForm();
    this.listOfLegalizationDocumentStatuses$ = this.dictionaryService.getLegalizationDocumentStatuses(false);
    this.listOfLegalizationStatuses$ = this.dictionaryService.getLegalizationStatuses(false);
    this.listOfWorkerLegalizationActiveFiles$ = this.legalizationService.getWorkerLegalizationActiveFiles(this.data.WorkerId);
    this.listOfLegalizationAlertStatusesForForms$ = this.dictionaryService.getLegalizationAlertStatusesForForms();

    if (this.data.LegalizationDocumentId) {
      this.loadLegalizationDocument(this.data.LegalizationDocumentId);
    }
    else {
      this.calculatePreSelectedAgreements();
      this.initializeDefaultValues();
    }

    if (this.data.IsCorrection) {
      this.disableFormFieldsForCorrection();
    }

  }

  isSendWorkerFileButtonEnabled(): boolean {
    return this.isDocumentUploaded && this.isFileCountValid() && !!this.legalizationDocumentTypeId.value && this.formGroup.valid;
  }

  initializeDefaultValues() {
    this.alertStatusId.setValue(LegalizationAlertStatusEnum.NoAlert);
  }

  public documentStartDateFilter = (date: Moment): boolean => !this.endDate.value || date.isSameOrBefore(this.endDate.value);

  public documentEndDateFilter = (date: Moment): boolean =>
    (!this.startDate.value || date.isSameOrAfter(this.startDate.value)) && date?.isSameOrAfter(moment().startOf('day'));

  createLegalizationDocument(): void {
    if (!this.files || this.formGroup.invalid) {
      return;
    }

    let legalizationDocument: LegalizationInformationDto = {
      WorkerId: this.data.WorkerId,
      LegalizationDocumentTypeId: this.legalizationDocumentTypeId.value,
      Description: this.description.value,
      StartDate: this.startDate.value,
      EndDate: this.endDate.value,
      Files: this.result,
      Name: this.legalizationFileTypeName,
      Comment: this.comment.value,
      LegalizationDocumentCategoryId: Number(this.documentCategory.value),
      LegalizationDocumentStatusId: Number(this.documentStatus.value),
      LegalizationsIds: this.legalizationsIds.value,
      DocumentNumber: this.documentNumber.value,
      SubmissionDate: this.submissionDate.value,
      StatusId: this.status.value,
      LegalizationAlertStatusId: this.alertStatusId.value,
    };

    this.onSave(legalizationDocument);
  }

  onClose(): void {
    this.dialogRef.close(false);
  }

  onSave(legalizationInformation: LegalizationInformationDto): void {
    this.spinner.show();

    const handleSuccess = () => {
      if (this.data.IsCorrection) {
        this.snackbar.openSuccessSnackBar('Legalization.SuccessfullyCorrected');
      } else {
        this.snackbar.openSuccessSnackBar('Legalization.SuccessfullySaved');
      }
      this.files = [];
      this.dialogRef.close({ isSuccess: true });
    };

    const observable = this.data.IsCorrection
      ? this.legalizationService.correctLegalizationDocumentByEmployee({
        Id: this.data.LegalizationDocumentId!,
        Files: legalizationInformation.Files,
      } as CorrectLegalizationDocumentBasicDto)
      : this.legalizationService.saveLegalizationInformation(legalizationInformation);

    observable
      .pipe(
        first(),
        finalize(() => this.spinner.hide()),
      )
      .subscribe({
        next: () => handleSuccess(),
      });
  }

  onLegalizationTypeSelected(type: LegalizationDocumentFileTypeDto): void {
    this.legalizationDocumentTypeId.setValue(type.Id);
    this.hasExpirationStartDate = type.HasExpirationStartDate || false;
    this.hasExpirationEndDate = type.HasExpirationEndDate || false;

    this.updateValidatorBasedOnCondition(this.startDate, this.hasExpirationStartDate);
    this.updateValidatorBasedOnCondition(this.endDate, this.hasExpirationEndDate);

    this.legalizationFileTypeName = type.Name;
    this.description.setValue(type.Description);

    this.documentCategory.setValue(type.LegalizationDocumentCategoryId?.toString() || '');
    this.alertStatusId.enable();
  }

  onAgreementsSelectionChange(selectedIds: number[]): void {
    this.legalizationsIds.setValue(selectedIds);
  }

  private calculatePreSelectedAgreements() {
    const selectedId = this.selectedDocument?.LegalizationId ? [this.selectedDocument.LegalizationId] : [];
    const preSelected = this.data.PreSelectedLegalizationsIds ?? [];

    this.preSelectedArray = [...new Set([...selectedId, ...preSelected])];
  }

  private createForm() {
    this.formGroup = this.formBuilder.group({
      legalizationDocumentTypeId: [null, [Validators.required]],
      description: [null],
      startDate: [null],
      endDate: [null],
      comment: [null, [Validators.maxLength(300)]],
      documentCategory: [null, [Validators.required]],
      documentStatus: [null, [Validators.required]],
      status: [LegalizationStatusEnum.InProgress, [Validators.required]],
      agreements: [[], [Validators.required]],
      documentNumber: [null, [Validators.maxLength(40)]],
      submissionDate: [null],
      alertStatusId: [{ disabled: true }, [Validators.required]],
    });
  }

  private disableFormFieldsForCorrection(): void {
    this.legalizationDocumentTypeId.disable();
    this.documentCategory.disable();
    this.comment.disable();
    this.startDate.disable();
    this.endDate.disable();
    this.documentNumber.disable();
    this.documentStatus.disable();
    this.submissionDate.disable();
    this.status.disable();
    this.alertStatusId.disable();
  }

  private loadLegalizationDocument(documentId: number): void {
    this.spinner.show();
    this.legalizationService
      .getLegalizationDocumentByLegalizer(documentId)
      .pipe(
        first(),
        finalize(() => this.spinner.hide()),
      )
      .subscribe((document) => {
        if (document) {
          this.selectedDocument = document;
          this.legalizationDocumentTypeId.setValue(document.LegalizationDocumentTypeId);
          this.startDate.setValue(document.StartDate);
          this.endDate.setValue(document.EndDate);
          this.documentNumber.setValue(document.DocumentNumber);
          this.comment.setValue(document.Comment);
          this.documentCategory.setValue(document.LegalizationDocumentCategoryId.toString());
          this.documentStatus.setValue(document.LegalizationDocumentStatusId);
          this.submissionDate.setValue(document.SubmissionDate);

          if (document.LegalizationAlertStatusId) {
            this.alertStatusId.setValue(document.LegalizationAlertStatusId);
          } else {
            this.alertStatusId.setValue(LegalizationAlertStatusEnum.NoAlert);
          }

          this.calculatePreSelectedAgreements();
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }
}
